import { useEffect, useState } from 'react';
import { api, enc } from '../api';
import { fmtWhen } from '../format';
import { useFeedback } from './feedback';
import { Area } from './fields';

// The deliverability preflight on its item (0038): the segment's mailbox, Vera's latest run with every check, what to
// change, Run the checks again after a DNS change, and a manager's Accept the risk with a note. The test send is a
// person's: it is ticked in the approval. The checks, not the boxes, are the gate.
type Check = { code: string; name: string; blocking: boolean; status: 'pass' | 'warn' | 'fail' | 'skip'; detail: string; fix: string | null };
type Run = { id: string; mailbox: string; domain: string; verdict: 'pass' | 'warn' | 'fail'; checks: Check[]; accepted: Array<{ code: string; by_name: string; note: string; at: string }>; created_at: string; ran_by: string | null };
type Data = { applies: boolean; segment: { id: string; name: string } | null; party: { id: string; name: string } | null; mailbox: string | null; run: Run | null; ok: boolean; why: string | null; age_days: number | null; valid_days: number; can_run: boolean; can_accept: boolean; failing: Array<{ code: string; name: string }>; why_no_mailbox: string | null; deliverable_status: string | null };
const word: Record<Check['status'], string> = { pass: 'pass', warn: 'warning', fail: 'fail', skip: 'not checked' };
const tagOf = (s: Check['status']) => (s === 'pass' ? 'ok' : s === 'warn' ? 'warn' : s === 'fail' ? 'bad' : '');

export function PreflightPanel({ itemId, reloadKey, onDone }: { itemId: string; reloadKey?: unknown; onDone?: () => void }) {
  const { openDialog, toast } = useFeedback();
  const [d, setD] = useState<Data | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => api<Data>('GET', `/work/${enc(itemId)}/preflight`).then((x) => { setD(x); setErr(''); }).catch((e) => setErr((e as Error).message));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [itemId, reloadKey]);
  if (err) return <section className="card pad"><h3>Deliverability preflight</h3><p className="small muted">{err}</p></section>;
  if (!d || !d.applies) return null;
  const run = async () => {
    setBusy(true);
    try { const r = await api('POST', `/work/${enc(itemId)}/preflight`, {}); toast(`Checks ran for ${r.run.mailbox}: ${r.run.verdict}`); await load(); onDone?.(); }
    catch (e) { toast((e as Error).message, true); }
    finally { setBusy(false); }
  };
  const accept = () => openDialog({
    title: 'Accept the risk', okLabel: 'Accept',
    body: (<>
      <p>The checks below failed. Accepting lets {d.mailbox} enroll anyway; your note is the record of why.</p>
      {d.failing.map((f) => <label key={f.code} className="chip-radio" style={{ display: 'block', marginBottom: 6 }}><input type="checkbox" name="codes" value={f.code} defaultChecked /> {f.name}</label>)}
      <Area name="note" label="Why this is acceptable" value="" required />
    </>),
    onSubmit: async (v) => {
      const codes = Array.isArray(v.codes) ? v.codes : v.codes ? [v.codes] : [];
      const r = await api('POST', `/work/${enc(itemId)}/preflight/accept`, { codes, note: v.note });
      toast(`Accepted; the verdict is now ${r.run.verdict}`); await load(); onDone?.();
    },
  });
  const acc = new Map((d.run?.accepted ?? []).map((a) => [a.code, a]));
  return (
    <section className="card pad">
      <h3>Deliverability preflight</h3>
      <div className="fact"><span>Mailbox</span><span>{d.mailbox || <span className="muted">{d.why_no_mailbox}</span>}</span></div>
      {d.run && <div className="fact"><span>Verdict</span><span><span className={`tag ${tagOf(d.run.verdict === 'warn' ? 'warn' : d.run.verdict)}`}>{d.run.verdict === 'warn' ? 'pass with warnings' : d.run.verdict}</span> · checked {fmtWhen(d.run.created_at)}{d.age_days !== null && d.age_days > 0 ? ` (${d.age_days} days ago, valid for ${d.valid_days})` : ''}</span></div>}
      {d.run && !d.ok && d.why && <p className="small overdue" style={{ marginTop: 8 }}>{d.why.charAt(0).toUpperCase() + d.why.slice(1)}.</p>}
      {!d.run && d.mailbox && <p className="small muted" style={{ marginTop: 8 }}>Vera runs the checks on the next tick; you can run them now.</p>}
      {d.run && (
        <div className="table-wrap" style={{ marginTop: 10 }}><table>
          <thead><tr><th>Check</th><th>Result</th><th>Found</th><th>Change</th></tr></thead>
          <tbody>{d.run.checks.map((c) => (
            <tr key={c.code}><td>{c.name}{!c.blocking ? <div className="small muted">reported; enforced at enrollment</div> : null}</td>
              <td><span className={`tag ${tagOf(c.status)}`}>{word[c.status]}</span>{acc.get(c.code) ? <div className="small muted">accepted by {acc.get(c.code)?.by_name}</div> : null}</td>
              <td className="small">{c.detail}</td><td className="small">{c.fix || '—'}</td></tr>
          ))}</tbody>
        </table></div>
      )}
      <p className="small muted" style={{ marginTop: 8 }}>The test send is yours: send one from the mailbox to a seed address and tick it in the approval. The newest run is the one the gate reads.</p>
      <div className="chips" style={{ marginTop: 10 }}>
        {d.can_run && <button onClick={run} disabled={busy}>{busy ? 'Checking…' : d.run ? 'Run the checks again' : 'Run the checks'}</button>}
        {d.can_accept && <button className="quiet" onClick={accept} disabled={busy}>Accept the risk</button>}
      </div>
    </section>
  );
}
